import { aCentimos, esMoneda, formatear } from '@mc/comercio'

import { ErrorDeLaApi } from '../api/nucleo'

/**
 * «Envíos y entregas» dicho en llano (CLAUDE.md §23): qué cubre una zona, su precio en la
 * moneda de la web, la tarifa por distancia antes de mandarla, el uso de Google Maps del mes y
 * lo que dijo el núcleo cuando algo no se pudo guardar. Funciones puras, con su prueba. Lo que
 * vale lo decide el núcleo (`src/entregas/`); esto solo pinta.
 */

export type TipoDeZona = 'a-mano' | 'distancia' | 'extranjero' | 'recojo' | 'zona'

export type ZonaEnPantalla = {
  id: number
  nombre: string
  tipo: TipoDeZona
  activa: boolean
  orden: number
  pais: null | string
  lugares: null | string[]
  lugaresDetalle: Array<{ codigo: string; camino: string }>
  precio: null | number
  plazo: null | string
  gratisDesde: null | number
  etiqueta: null | string
  direccion: null | string
  horario: null | string
  /** Su nombre, su plazo y su horario en los otros idiomas de la web (§37). */
  traducciones?: null | Partial<Record<'en' | 'es' | 'pt', Record<string, string>>>
  /** El envío por distancia: precio base + precio por kilómetro empezado, dentro del radio. */
  precioBase: null | number
  precioPorKm: null | number
  radioMaximoKm: null | number
  masAlla: 'no-enviar' | 'zonas'
  /** La dirección de la tienda, ubicada por el núcleo con Google Maps (lat/lng los pone el servidor). */
  origen: null | { direccion: null | string; lat: null | number; lng: null | number; placeId: null | string }
}

export type Pantalla = {
  sitio: number
  tienda: boolean
  vendeEnLinea: boolean
  moneda: string
  /** Si Wixy tiene Google Maps configurado: sin él, el envío por distancia no se puede activar. */
  googleDisponible: boolean
  paises: Array<{ codigo: string; nombre: string }>
  zonas: ZonaEnPantalla[]
}

export type TipoDeConsumoDeMapas = 'autocompletar' | 'detalle' | 'geocodificar' | 'mapa-estatico' | 'ruta'

/** Lo que responde `GET /api/entregas/mapas/consumo`. */
export type UsoDeMapas = {
  sitio: number
  mes: string
  googleDisponible: boolean
  porTipo: Record<TipoDeConsumoDeMapas, { cantidad: number; costeEstimadoCentimos: number }>
  total: number
  costeEstimadoCentimos: number
  cuota: number
  /** `null` cuando el plan no incluye mapas (cuota 0). */
  porcentaje: null | number
  alertaPorcentaje: number
  alLlegarAlTope: 'bloquear' | 'fallback'
}

/** Cómo se llama cada tipo de consulta para quien lee la tarjeta. */
export const ETIQUETAS_DE_CONSUMO: Record<TipoDeConsumoDeMapas, string> = {
  autocompletar: 'Sugerencias de dirección',
  detalle: 'Direcciones elegidas',
  ruta: 'Distancias calculadas',
  geocodificar: 'Direcciones ubicadas',
  'mapa-estatico': 'Mapas en la web',
}

/** Un precio escrito en el panel (19.9) → «S/ 19,90», con los decimales de la moneda. */
export const precioEnLlano = (precio: null | number | undefined, moneda: string): string => {
  if (typeof precio !== 'number') return '—'
  if (precio === 0) return 'Gratis'
  return esMoneda(moneda) ? formatear(aCentimos(precio, moneda), moneda) : `${precio} ${moneda}`
}

/** Qué cubre una zona, en una frase: «Perú › Lima (y 3 lugares más)», «Todo Perú», «Los países sin zona propia». */
export const coberturaEnLlano = (z: Pick<ZonaEnPantalla, 'lugaresDetalle' | 'pais' | 'tipo'>, paises: Pantalla['paises']): string => {
  const pais = paises.find((p) => p.codigo === z.pais)?.nombre ?? z.pais ?? ''
  if (z.tipo === 'extranjero') return 'Los países donde no tienes una zona propia'
  if (z.tipo === 'a-mano') return 'El comprador la elige de una lista, por su nombre'
  if (z.tipo !== 'zona') return pais
  if (z.lugaresDetalle.length === 0) return `Todo ${pais}`
  const [primero, ...resto] = z.lugaresDetalle
  return `${pais} › ${primero!.camino}${resto.length > 0 ? ` (y ${resto.length === 1 ? 'otro lugar' : `${resto.length} lugares más`})` : ''}`
}

const numeroDe = (texto: string): number => Number(texto.replace(',', '.'))

/** La tarifa por distancia tal como se escribe, revisada antes de mandarla: el núcleo la vuelve a revisar con la moneda de la web. */
export const revisarTarifaPorDistancia = (t: { precioBase: string; precioPorKm: string; radioMaximoKm: string }): { datos: { precioBase: number; precioPorKm: number; radioMaximoKm: number } | null; error: null | { campo: string; mensaje: string } } => {
  const base = numeroDe(t.precioBase)
  if (t.precioBase.trim() === '' || !Number.isFinite(base) || base < 0) return { datos: null, error: { campo: 'precioBase', mensaje: 'Escribe el precio base (0 si solo cobras por kilómetro).' } }
  const porKm = numeroDe(t.precioPorKm)
  if (t.precioPorKm.trim() === '' || !Number.isFinite(porKm) || porKm < 0) return { datos: null, error: { campo: 'precioPorKm', mensaje: 'Escribe cuánto cobras por cada kilómetro (0 si solo cobras el precio base).' } }
  const radio = numeroDe(t.radioMaximoKm)
  if (t.radioMaximoKm.trim() === '' || !Number.isFinite(radio) || radio <= 0) return { datos: null, error: { campo: 'radioMaximoKm', mensaje: 'Escribe hasta cuántos kilómetros envías (más de 0).' } }
  return { datos: { precioBase: base, precioPorKm: porKm, radioMaximoKm: radio }, error: null }
}

/** «10 soles más 0,50 por kilómetro, hasta 15 km»: la tarifa de un vistazo. */
export const tarifaEnLlano = (z: Pick<ZonaEnPantalla, 'precioBase' | 'precioPorKm' | 'radioMaximoKm'>, moneda: string): string => {
  if (typeof z.precioBase !== 'number' || typeof z.radioMaximoKm !== 'number') return 'Sin tarifa todavía'
  const porKm = typeof z.precioPorKm === 'number' && z.precioPorKm > 0 ? ` más ${precioEnLlano(z.precioPorKm, moneda)} por kilómetro` : ''
  return `${precioEnLlano(z.precioBase, moneda)}${porKm}, hasta ${String(z.radioMaximoKm).replace('.', ',')} km`
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

/** `2026-10` → «octubre de 2026». */
export const mesEnLlano = (mes: string): string => {
  const [anio, m] = mes.split('-').map(Number)
  return m && m >= 1 && m <= 12 ? `${MESES[m - 1]} de ${anio}` : mes
}

/** Qué pasa con los mapas de esta web: cuánto queda y qué ocurre al llegar al tope, en una frase para la tarjeta. */
export const usoDeMapasEnLlano = (u: Pick<UsoDeMapas, 'alertaPorcentaje' | 'alLlegarAlTope' | 'cuota' | 'googleDisponible' | 'total'>): { resumen: string; alTope: string; tono: 'aviso' | 'error' | 'info' | 'ok' } => {
  if (!u.googleDisponible) return { resumen: 'Google Maps no está disponible ahora mismo.', alTope: 'El envío por distancia y las sugerencias de dirección no funcionan hasta que lo esté; el carrito pide los lugares en desplegables.', tono: 'aviso' }
  if (u.cuota <= 0) return { resumen: 'Tu plan no incluye consultas a Google Maps.', alTope: 'El envío por distancia no se puede activar y el bloque de mapa de tu web solo enlaza a Google Maps. Un plan superior las incluye.', tono: 'info' }
  const porcentaje = Math.min(100, Math.round((u.total / u.cuota) * 100))
  const quedan = Math.max(0, u.cuota - u.total)
  const resumen = porcentaje >= 100 ? `Has usado las ${u.cuota} consultas de este mes.` : `${u.total} de ${u.cuota} consultas este mes (${porcentaje} %). Quedan ${quedan}.`
  const alTope = [
    `Al llegar a ${u.cuota}, el carrito pide el país y el distrito en desplegables y cobra por tus zonas de lugares; las sugerencias de dirección vuelven el mes que viene.`,
    u.alLlegarAlTope === 'fallback' ? 'Además avisamos al equipo de soporte, que puede ampliar la cuota.' : '',
    `Te avisamos por correo al llegar al ${u.alertaPorcentaje} %.`,
  ]
    .filter(Boolean)
    .join(' ')
  return { resumen, alTope, tono: porcentaje >= 100 ? 'error' : porcentaje >= u.alertaPorcentaje ? 'aviso' : 'ok' }
}

/** Mover una zona arriba o abajo: el orden nuevo de todas, de 10 en 10, y solo las que cambian. */
export const reordenar = (zonas: Array<Pick<ZonaEnPantalla, 'id' | 'orden'>>, id: number, sentido: -1 | 1): Array<{ id: number; orden: number }> => {
  const lista = [...zonas].sort((a, b) => a.orden - b.orden || a.id - b.id)
  const i = lista.findIndex((z) => z.id === id)
  const j = i + sentido
  if (i < 0 || j < 0 || j >= lista.length) return []
  ;[lista[i], lista[j]] = [lista[j]!, lista[i]!]
  return lista.map((z, k) => ({ id: z.id, orden: k * 10 })).filter((z) => zonas.find((x) => x.id === z.id)!.orden !== z.orden)
}

/**
 * Lo que dijo el núcleo, para enseñarlo: si es un error de validación, el mensaje del campo
 * (que dice qué hacer) y su ruta; si no, el mensaje del error.
 */
export const errorDeGuardado = (e: unknown): { mensaje: string; campo: null | string } => {
  if (e instanceof ErrorDeLaApi) {
    const errores = Array.isArray(e.datos.errors) ? (e.datos.errors as Array<{ message?: string; path?: string }>) : []
    const primero = errores.find((x) => typeof x.message === 'string' && x.message)
    if (primero) return { mensaje: primero.message!, campo: primero.path ?? null }
    return { mensaje: e.message, campo: null }
  }
  return { mensaje: 'No se ha podido guardar. Vuelve a intentarlo en un momento.', campo: null }
}
